import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/app/components/ui/Alert';
import { Button } from '@/app/components/ui/Button';
import { Card } from '@/app/components/ui/Card';
import { PageHeader } from '@/app/components/ui/PageHeader';
import { Spinner } from '@/app/components/ui/Spinner';
import { getEntitlement } from '@/features/subscription/api';

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // ~60s, per *Abonnement*.

/**
 * `/subscription/return` — where Mollie sends the browser back. The
 * subscription only exists once the webhook has reported "paid", so this
 * page polls `entitlement` every 2s for up to ~60s rather than trusting the
 * redirect alone.
 */
export function ReturnPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'waiting' | 'success'>('waiting');
  const attemptsRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = () => {
      getEntitlement()
        .then((entitlement) => {
          if (cancelled) return;
          if (entitlement.active) {
            setStatus('success');
            return;
          }
          attemptsRef.current += 1;
          if (attemptsRef.current < MAX_POLLS) {
            timer = setTimeout(poll, POLL_INTERVAL_MS);
          }
        })
        .catch(() => {
          if (cancelled) return;
          attemptsRef.current += 1;
          if (attemptsRef.current < MAX_POLLS) {
            timer = setTimeout(poll, POLL_INTERVAL_MS);
          }
        });
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <section className="page flex flex-col gap-6">
      <PageHeader title={t('subscription.return.title')} />
      <Card>
        {status === 'waiting' ? (
          <div className="flex items-center gap-3">
            <Spinner />
            <p className="text-ink text-sm">{t('subscription.return.waiting')}</p>
          </div>
        ) : (
          <Alert tone="success">{t('subscription.return.success')}</Alert>
        )}
        <Button as={Link} to="/subscription" variant="secondary" className="mt-4">
          {t('subscription.return.backToSubscription')}
        </Button>
      </Card>
    </section>
  );
}
